import _ from 'underscore';
import React, { useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import {
  Menu,
  MenuButton,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuTrigger,
  Caption1 as Text,
  Dropdown,
  DropdownProps,
  Option,
  OptionGroup,
  Tooltip
} from '@fluentui/react-components';

import { usePreferenceMetrics } from './use-preference-metrics';
import { CompetitionFilterProps } from '../utils/types';
import { withCompetitorsData } from '../data/with-competitors-data';
import { useFilterStyles } from '../styles/filter-styles';

export function CompetitionFilter({
  isLoading,
  state,
  setState,
  competitors,
  i18n,
}: CompetitionFilterProps) {
  const styles = useFilterStyles();

  const [selectedCompetitors, setSelectedCompetitors] = React.useState<string[]>();

  const onMetricSet = useCallback((metric: string) => {
    setState(s => ({
      ...s,
      metric,
    }));
  }, [setState]);

  const {
    metrics,
    selectedMetric,
    setMetric,
  } = usePreferenceMetrics({ onMetricSet });

  useEffect(() => {
    if (!state && !isLoading && selectedMetric) {
      const initCompetitors = _.first(competitors, 4) || [];
      setSelectedCompetitors(initCompetitors);
      setState({
        metric: selectedMetric?.metric,
        competitors: initCompetitors,
      });
    }
  }, [selectedMetric, isLoading, state, setState, competitors]);

  const onSelect: DropdownProps['onOptionSelect'] = (event, data) => {
    setSelectedCompetitors(data.selectedOptions);
    setState(s => ({
      ...s,
      competitors: data.selectedOptions,
    }));
  };

  const dropdownButtonText = useMemo(() => {
    const text = selectedCompetitors?.length > 0 ?
      i18n.getString(_TL_('{{selected}} similar advertisers'), { selected: selectedCompetitors?.length })
      : i18n.getString(_TL_('Compare up to 4 similar advertisers'));
    return (
      <Tooltip content={text} relationship="label">
        <Text className={classNames('competitor-dropdown-button-text', styles.buttonText)}>{text}</Text>
      </Tooltip>
    );
  }, [selectedCompetitors, styles, i18n]);

  if (!state) {
    return <></>;
  }

  return (
    <div className={classNames('ba-competition-filter', styles.container)}>
      <div>
        <Dropdown
          multiselect
          selectedOptions={selectedCompetitors}
          onOptionSelect={onSelect}
          button={dropdownButtonText}
          size="small"
          listbox={{ className: styles.listbox }}
          disabled={isLoading}
          className={classNames('competitors-dropdown', styles.dropdown)}
        >
          <OptionGroup className="competitor-label" label={i18n.getString(_TL_('Compare up to 4 similar advertisers'))}>
            {competitors.map(option => (
              <Option className="competitor-option" key={option} disabled={selectedCompetitors?.length >= 4 && !selectedCompetitors.includes(option)}>{option}</Option>
            ))}
          </OptionGroup>
        </Dropdown>
      </div>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <MenuButton size="large" className={classNames('by-metric', styles.button)} appearance="transparent" disabled={isLoading}>
            <Tooltip content={selectedMetric.value} relationship="label">
              <Text className={classNames('metric-filter-button-text', styles.buttonText)}>{selectedMetric.value}</Text>
            </Tooltip>
          </MenuButton>
        </MenuTrigger>
        <MenuPopover className="metrics">
          <MenuList>
            {
              _.map(metrics, metric =>
                <MenuItem className={classNames('metric-option', metric.metric)} onClick={() => setMetric(metric)} key={metric.metric}>{metric.value}</MenuItem>)
            }
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
}

CompetitionFilter.defaultProps = {
  state: undefined,
  competitors: [],
};

export const CompetitionFilterWithData = withCompetitorsData(CompetitionFilter);
